import { create } from "zustand";
import { Creator } from "../../types/types";
import { toast } from "sonner";
import { useCampaignStore } from "../campaign/campaignStore";

interface CreatorsState {
  creators: Creator[];
  isLoading: boolean;
  error: string | null;
  fetchCreators: (campaignId: string, token: string) => Promise<Creator[]>;
  setCreators: (creators: Creator[]) => void;
  patchCreator: (campaignCreatorId: string, partial: Partial<Creator>) => void;
  deleteCreators: (
    campaignCreatorIds: string[],
    token: string
  ) => Promise<boolean>;
  transferCreators: (
    campaignCreatorIds: string[],
    targetCampaignId: string,
    organizationId: string,
    token: string
  ) => Promise<boolean>;
  updateCreatorField: (
    campaignCreatorId: string,
    fieldName: string,
    value: string,
    accepted: boolean | null | undefined,
    token: string
  ) => Promise<boolean>;
  dismissHandoff: (
    campaignCreatorId: string,
    token: string
  ) => Promise<boolean>;
  reset: () => void;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export const useCreatorsStore = create<CreatorsState>((set, get) => ({
  creators: [],
  isLoading: false,
  error: null,

  fetchCreators: async (campaignId: string, token: string) => {
    set({ isLoading: true, error: null, creators: [] });
    try {
      const response = await fetch(`${API_BASE_URL}/api/search/getCreators`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ campaign_id: campaignId }),
      });

      if (!response.ok) throw new Error("Failed to fetch creators");
      const data = await response.json();
      // console.log(data);

      set({ creators: data.data, isLoading: false });
      return data.data;
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
      return [];
    }
  },

  setCreators: (creators: Creator[]) => set({ creators }),

  // Patch a single creator in place (optimistic updates) without refetching the
  // whole list — avoids the full-table flash that fetchCreators causes.
  patchCreator: (campaignCreatorId: string, partial: Partial<Creator>) =>
    set((state) => ({
      creators: state.creators.map((creator) =>
        creator._id === campaignCreatorId
          ? { ...creator, ...partial }
          : creator
      ),
    })),

  deleteCreators: async (campaignCreatorIds: string[], token: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/campaigns/deleteCreators`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ campaign_creator_ids: campaignCreatorIds }),
        }
      );

      if (!response.ok) throw new Error("Failed to delete creators");

      const data = await response.json();
      if (data.success) {
        toast.success("Creators deleted successfully");
        set((state) => ({
          creators: state.creators.filter(
            (creator) => !campaignCreatorIds.includes(creator._id)
          ),
          isLoading: false,
        }));
        return true;
      } else {
        toast.error("Failed to delete creators");
        set({ error: data.message, isLoading: false });
        return false;
      }
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
      return false;
    }
  },

  transferCreators: async (
    campaignCreatorIds: string[],
    targetCampaignId: string,
    organizationId: string,
    token: string
  ) => {
    set({ isLoading: true, error: null });
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/campaigns/transferCreators`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            campaign_creator_ids: campaignCreatorIds,
            target_campaign_id: targetCampaignId,
            organization_id: organizationId,
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to transfer creators");

      const data = await response.json();
      if (data.success) {
        const transferredCount = data.transferred ?? 0;
        const skipped =
          (data.skipped_duplicate ?? 0) +
          (data.skipped_not_pending ?? 0) +
          (data.skipped_platform ?? 0);
        if (transferredCount > 0) {
          toast.success(
            `Transferred ${transferredCount} creator${
              transferredCount === 1 ? "" : "s"
            }${skipped ? `, ${skipped} skipped` : ""}`
          );
        } else {
          toast.info(
            skipped
              ? `No creators transferred: ${skipped} skipped (already in target or not transferable)`
              : "No creators transferred"
          );
        }
        // Reload the source campaign table from the server so it reflects the
        // true result. We can't reconcile optimistically: the backend may skip
        // duplicates (creator already in the target), and those aren't
        // detectable client-side, so blindly dropping the requested ids would
        // wrongly remove them from this campaign.
        if (transferredCount > 0) {
          const sourceCampaignId = useCampaignStore.getState().campaignId;
          if (sourceCampaignId) {
            await get().fetchCreators(sourceCampaignId, token);
          }
        }
        set({ isLoading: false });
        return true;
      } else {
        toast.error(data.error || "Failed to transfer creators");
        set({ error: data.error, isLoading: false });
        return false;
      }
    } catch (error) {
      toast.error("Failed to transfer creators");
      set({ error: (error as Error).message, isLoading: false });
      return false;
    }
  },

  updateCreatorField: async (
    campaignCreatorId: string,
    fieldName: string,
    value: string,
    accepted: boolean | null | undefined,
    token: string
  ) => {
    const snapshot = useCreatorsStore.getState().creators;

    set((state) => ({
      creators: state.creators.map((creator) => {
        if (creator._id !== campaignCreatorId) return creator;
        const existingFields = creator.fields || [];
        const hasField = existingFields.some((f) => f.name === fieldName);
        const nextFields = hasField
          ? existingFields.map((f) => {
              if (f.name !== fieldName) return f;
              const updated = { ...f, value };
              if (accepted === null || accepted === undefined) {
                delete updated.accepted;
              } else {
                updated.accepted = accepted;
              }
              return updated;
            })
          : [
              ...existingFields,
              {
                name: fieldName,
                value,
                ...(accepted === null || accepted === undefined
                  ? {}
                  : { accepted }),
              },
            ];
        return { ...creator, fields: nextFields };
      }),
    }));

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/campaigns/updateCreatorField`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            campaign_creator_id: campaignCreatorId,
            field_name: fieldName,
            value,
            accepted: accepted ?? null,
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to update field");
      const data = await response.json();
      if (!data.success) throw new Error(data.error || "Failed to update field");
      return true;
    } catch (error) {
      set({ creators: snapshot });
      toast.error("Failed to save field");
      console.error("Error updating creator field:", error);
      return false;
    }
  },

  dismissHandoff: async (campaignCreatorId: string, token: string) => {
    const snapshot = useCreatorsStore.getState().creators;

    set((state) => ({
      creators: state.creators.map((creator) =>
        creator._id === campaignCreatorId
          ? { ...creator, hand_off_to_user: false, autopilot: true }
          : creator
      ),
    }));

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/campaigns/dismissHandoff`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ campaign_creator_id: campaignCreatorId }),
        }
      );

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to dismiss handoff");
      }

      toast.success("Handoff dismissed & Autopilot turned ON");
      return true;
    } catch (error) {
      set({ creators: snapshot });
      toast.error("Failed to dismiss handoff");
      console.error("Error dismissing handoff:", error);
      return false;
    }
  },

  reset: () => set({ creators: [], isLoading: false, error: null }),
}));
